import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Building2, ArrowLeft, User, Mail, Phone, MapPin,
  Calendar, Clock, CheckCircle, XCircle, PauseCircle, AlertCircle,
  Shield, KeyRound, UserX, UserCheck, Loader2, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const statusConfig = {
  trialing: { label: 'Trial', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Clock },
  active: { label: 'Aktiv', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  past_due: { label: 'Überfällig', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: AlertCircle },
  cancelled: { label: 'Gekündigt', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: XCircle },
  suspended: { label: 'Gesperrt', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: PauseCircle },
  expired: { label: 'Abgelaufen', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
};

const planLabels = {
  starter: 'Starter (29€/Monat)',
  professional: 'Professional (59€/Monat)',
  enterprise: 'Enterprise (99€/Monat)'
};

export default function AdminAccountDetail() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accountData, setAccountData] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  
  // Dialog states
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendLockType, setSuspendLockType] = useState('soft');  // 'soft' oder 'hard'
  const [impersonateDialog, setImpersonateDialog] = useState(false);
  const [impersonateReason, setImpersonateReason] = useState('');

  useEffect(() => {
    fetchAccountDetails();
  }, [tenantId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');  // Korrigiert von cashx_token
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchAccountDetails = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/accounts/${tenantId}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          toast.error('Account nicht gefunden');
          navigate('/admin/accounts');
          return;
        }
        throw new Error('Failed to fetch account');
      }
      
      const data = await response.json();
      setAccountData(data);
    } catch (error) {
      toast.error('Fehler beim Laden des Accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) {
      toast.error('Bitte geben Sie einen Grund an');
      return;
    }
    
    setActionLoading('suspend');
    try {
      const response = await fetch(`${API_URL}/api/admin/accounts/${tenantId}/suspend`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          reason: suspendReason,
          lock_type: suspendLockType  // "soft" oder "hard"
        })
      });
      
      if (!response.ok) throw new Error('Failed to suspend');
      
      const lockLabel = suspendLockType === 'soft' ? 'Soft Lock' : 'Hard Lock';
      toast.success(`Account gesperrt (${lockLabel})`);
      setSuspendDialog(false);
      setSuspendReason('');
      setSuspendLockType('soft');
      fetchAccountDetails();
    } catch (error) {
      toast.error('Fehler beim Sperren');
    } finally {
      setActionLoading('');
    }
  };

  const handleUnsuspend = async () => {
    setActionLoading('unsuspend');
    try {
      const response = await fetch(`${API_URL}/api/admin/accounts/${tenantId}/unsuspend`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to unsuspend');
      
      toast.success('Account entsperrt');
      fetchAccountDetails();
    } catch (error) {
      toast.error('Fehler beim Entsperren');
    } finally {
      setActionLoading('');
    }
  };

  const handleImpersonate = async () => {
    setActionLoading('impersonate');
    try {
      const response = await fetch(`${API_URL}/api/admin/accounts/${tenantId}/impersonate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: impersonateReason || 'Support-Anfrage' })
      });
      
      if (!response.ok) throw new Error('Failed to impersonate');
      
      const data = await response.json();
      
      // Store current admin token
      localStorage.setItem('cashx_admin_token', localStorage.getItem('access_token'));
      localStorage.setItem('cashx_admin_user', localStorage.getItem('cashx_user'));
      
      // Set impersonation token
      localStorage.setItem('access_token', data.impersonation_token);  // Korrigiert
      localStorage.setItem('cashx_user', JSON.stringify(data.target_user));
      localStorage.setItem('cashx_impersonation', JSON.stringify({
        session_id: data.session_id,
        target_tenant: data.target_tenant,
        expires_at: data.expires_at
      }));
      
      toast.success('Support-Session gestartet', {
        description: `Sie sind jetzt als ${data.target_user.name} angemeldet`
      });
      
      setImpersonateDialog(false);
      navigate('/app');
    } catch (error) {
      toast.error('Fehler beim Starten der Session');
    } finally {
      setActionLoading('');
    }
  };

  const handleResetPassword = async (userId) => {
    setActionLoading(`reset-${userId}`);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/accounts/${tenantId}/users/${userId}/reset-password`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      
      if (!response.ok) throw new Error('Failed to reset');
      
      toast.success('Passwort-Reset E-Mail gesendet');
    } catch (error) {
      toast.error('Fehler beim Senden');
    } finally {
      setActionLoading('');
    }
  };

  const handleToggleUserActive = async (userId, isActive) => {
    const action = isActive ? 'deactivate' : 'reactivate';
    setActionLoading(`toggle-${userId}`);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/accounts/${tenantId}/users/${userId}/${action}`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      
      if (!response.ok) throw new Error('Failed to toggle');
      
      toast.success(isActive ? 'Benutzer deaktiviert' : 'Benutzer aktiviert');
      fetchAccountDetails();
    } catch (error) {
      toast.error('Fehler bei der Aktion');
    } finally {
      setActionLoading('');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <Skeleton className="h-8 w-48 mb-4 bg-slate-700" />
        <Skeleton className="h-64 w-full bg-slate-700 rounded-lg" />
      </div>
    );
  }

  if (!accountData) return null;

  const { tenant, users, statistics } = accountData;
  const config = statusConfig[tenant.account_status] || statusConfig.active;
  const StatusIcon = config.icon;

  return (
    <div className="p-6 md:p-8">
      {/* Back Button */}
      <Link 
        to="/admin/accounts"
        className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Zurück zu Accounts
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 bg-slate-700 rounded-xl flex items-center justify-center">
            <Building2 className="h-8 w-8 text-slate-300" />
          </div>
          <div>
            <h1 
              className="text-2xl font-bold"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              data-testid="account-detail-title"
            >
              {tenant.company_name}
            </h1>
            <p className="text-slate-400">{tenant.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={`${config.color} border`}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {config.label}
              </Badge>
              <Badge variant="outline" className="border-slate-600 text-slate-400">
                {planLabels[tenant.plan]}
              </Badge>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setImpersonateDialog(true)}
            data-testid="impersonate-btn"
            className="border-[#2F80FF] text-[#2F80FF] hover:bg-[#2F80FF]/10"
          >
            <Eye className="h-4 w-4 mr-2" />
            Als User anmelden
          </Button>
          
          {tenant.account_status === 'suspended' ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleUnsuspend}
              disabled={actionLoading === 'unsuspend'}
              data-testid="unsuspend-btn"
              className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10"
            >
              {actionLoading === 'unsuspend' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Entsperren
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSuspendDialog(true)}
              data-testid="suspend-btn"
              className="border-red-500 text-red-400 hover:bg-red-500/10"
            >
              <PauseCircle className="h-4 w-4 mr-2" />
              Sperren
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Account Info */}
        <Card 
          className="lg:col-span-2 p-6" 
          style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
        >
          <h2 className="text-lg font-semibold mb-4">Account-Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-400 mb-1">Ansprechpartner</p>
                <p className="flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-500" />
                  {tenant.contact_name}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-400 mb-1">E-Mail</p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-500" />
                  {tenant.email}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-400 mb-1">Telefon</p>
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-500" />
                  {tenant.phone || '-'}
                </p>
              </div>
              {tenant.address && (
                <div>
                  <p className="text-sm text-slate-400 mb-1">Adresse</p>
                  <p className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-slate-500 mt-0.5" />
                    <span>
                      {tenant.address.street}<br />
                      {tenant.address.postal_code} {tenant.address.city}
                    </span>
                  </p>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-400 mb-1">Registriert</p>
                <p className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  {formatDate(tenant.created_at)}
                </p>
              </div>
              {tenant.trial_ends_at && (
                <div>
                  <p className="text-sm text-slate-400 mb-1">Trial endet</p>
                  <p className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-500" />
                    {formatDate(tenant.trial_ends_at)}
                  </p>
                </div>
              )}
              {tenant.current_period_end && (
                <div>
                  <p className="text-sm text-slate-400 mb-1">Nächste Abrechnung</p>
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-slate-500" />
                    {formatDate(tenant.current_period_end)}
                  </p>
                </div>
              )}
              {tenant.suspended_at && (
                <div>
                  <p className="text-sm text-slate-400 mb-1">Gesperrt seit</p>
                  <p className="flex items-center gap-2 text-red-400">
                    <PauseCircle className="h-4 w-4" />
                    {formatDate(tenant.suspended_at)}
                  </p>
                  {tenant.lock_type && (
                    <p className="text-sm font-semibold mt-1 ml-6">
                      {tenant.lock_type === 'hard' ? '🔒 Hard Lock (Login blockiert)' : '⚠️ Soft Lock (POS gesperrt)'}
                    </p>
                  )}
                  {tenant.suspension_reason && (
                    <p className="text-sm text-slate-500 mt-1 ml-6">
                      Grund: {tenant.suspension_reason}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <Separator className="my-6 bg-slate-700" />
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#0E1628' }}>
              <p className="text-2xl font-bold">{statistics.user_count}</p>
              <p className="text-sm text-slate-400">Benutzer</p>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#0E1628' }}>
              <p className="text-2xl font-bold">{statistics.total_orders}</p>
              <p className="text-sm text-slate-400">Bestellungen</p>
            </div>
          </div>
        </Card>

        {/* Users List */}
        <Card 
          className="p-6" 
          style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
        >
          <h2 className="text-lg font-semibold mb-4">Benutzer ({users.length})</h2>
          
          <div className="space-y-3">
            {users.map((user) => (
              <div 
                key={user.user_id}
                className="p-3 rounded-lg"
                style={{ backgroundColor: '#0E1628' }}
                data-testid={`user-card-${user.user_id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-sm text-slate-400">{user.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          user.role === 'owner' 
                            ? 'border-amber-500 text-amber-400' 
                            : 'border-slate-600 text-slate-400'
                        }`}
                      >
                        {user.role}
                      </Badge>
                      {!user.is_active && (
                        <Badge variant="outline" className="text-xs border-red-500 text-red-400">
                          Deaktiviert
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleResetPassword(user.user_id)}
                    disabled={actionLoading === `reset-${user.user_id}`}
                    className="text-xs text-slate-400 hover:text-white"
                  >
                    {actionLoading === `reset-${user.user_id}` ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <KeyRound className="h-3 w-3 mr-1" />
                    )}
                    PW Reset
                  </Button>
                  
                  {user.role !== 'owner' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleUserActive(user.user_id, user.is_active)}
                      disabled={actionLoading === `toggle-${user.user_id}`}
                      className={`text-xs ${
                        user.is_active 
                          ? 'text-red-400 hover:text-red-300' 
                          : 'text-emerald-400 hover:text-emerald-300'
                      }`}
                    >
                      {actionLoading === `toggle-${user.user_id}` ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : user.is_active ? (
                        <UserX className="h-3 w-3 mr-1" />
                      ) : (
                        <UserCheck className="h-3 w-3 mr-1" />
                      )}
                      {user.is_active ? 'Deaktivieren' : 'Aktivieren'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialog} onOpenChange={setSuspendDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700">
          <DialogHeader>
            <DialogTitle>Account sperren</DialogTitle>
            <DialogDescription className="text-slate-400">
              Wählen Sie die Art der Sperrung und geben Sie einen Grund an.
            </DialogDescription>
          </DialogHeader>
          
          {/* Lock Type Selection */}
          <div className="space-y-4">
            <div className="flex gap-3">
              <button
                onClick={() => setSuspendLockType('soft')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  suspendLockType === 'soft'
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-slate-600 bg-slate-800/50 hover:border-slate-500'
                }`}
                data-testid="lock-type-soft"
              >
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className={`h-4 w-4 ${suspendLockType === 'soft' ? 'text-amber-400' : 'text-slate-400'}`} />
                  <span className={`font-semibold ${suspendLockType === 'soft' ? 'text-white' : 'text-slate-300'}`}>
                    Soft Lock
                  </span>
                </div>
                <p className="text-xs text-slate-400 text-left">
                  Login erlaubt, aber POS gesperrt. Für Zahlungsprobleme.
                </p>
              </button>
              
              <button
                onClick={() => setSuspendLockType('hard')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  suspendLockType === 'hard'
                    ? 'border-red-500 bg-red-500/10'
                    : 'border-slate-600 bg-slate-800/50 hover:border-slate-500'
                }`}
                data-testid="lock-type-hard"
              >
                <div className="flex items-center gap-2 mb-1">
                  <XCircle className={`h-4 w-4 ${suspendLockType === 'hard' ? 'text-red-400' : 'text-slate-400'}`} />
                  <span className={`font-semibold ${suspendLockType === 'hard' ? 'text-white' : 'text-slate-300'}`}>
                    Hard Lock
                  </span>
                </div>
                <p className="text-xs text-slate-400 text-left">
                  Login komplett blockiert. Für Betrug/Sicherheit.
                </p>
              </button>
            </div>
            
            <Textarea
              placeholder="Grund für die Sperrung..."
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              className="bg-[#0E1628] border-slate-700 text-white"
              data-testid="suspend-reason-input"
            />
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setSuspendDialog(false);
                setSuspendReason('');
                setSuspendLockType('soft');
              }}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSuspend}
              disabled={actionLoading === 'suspend' || !suspendReason.trim()}
              className={suspendLockType === 'hard' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'}
              data-testid="confirm-suspend-button"
            >
              {actionLoading === 'suspend' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <PauseCircle className="h-4 w-4 mr-2" />
              )}
              {suspendLockType === 'hard' ? 'Hard Lock aktivieren' : 'Soft Lock aktivieren'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonate Dialog */}
      <Dialog open={impersonateDialog} onOpenChange={setImpersonateDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-400" />
              Support-Session starten
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Sie werden für 30 Minuten als Account-Inhaber angemeldet. 
              Alle Aktionen werden protokolliert.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Grund für die Session (optional)..."
            value={impersonateReason}
            onChange={(e) => setImpersonateReason(e.target.value)}
            className="bg-[#0E1628] border-slate-700 text-white"
          />
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <p className="text-sm text-amber-400">
              ⚠️ Alle Ihre Aktionen in dieser Session werden im Audit-Log protokolliert.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setImpersonateDialog(false)}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleImpersonate}
              disabled={actionLoading === 'impersonate'}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {actionLoading === 'impersonate' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Eye className="h-4 w-4 mr-2" />
              )}
              Session starten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
